// The row-handling logic behind Signals › Companies — no React, no DOM, so
// `node --test` can load this exact file (test/signal_queue_logic.test.mjs).
// views/company_signals.jsx reads these off window.SignalQueueLogic.

// A company can come back once per owner (the queue groups by both), so the
// owner is part of the key.
const rowKey = (r) => `${r.company_id}:${r.assigned_user_id || ''}`;

const spanLabel = (days) => (days < 45 ? `${days}d` : days < 700 ? `${Math.round(days / 30)} mo` : `${(days / 365).toFixed(1)} yr`);

// How far the owner's own revisit date is from today: "Overdue 9 mo", "Due in 12d".
const queueDue = (due, now = new Date()) => {
  if (!due) return null;
  const d = new Date(due + 'T00:00:00');
  const today = new Date(now); today.setHours(0, 0, 0, 0);
  const days = Math.round((d - today) / 86400000);
  if (days < 0) return { text: `Overdue ${spanLabel(-days)}`, hue: 'rose' };
  if (days === 0) return { text: 'Due today', hue: 'rose' };
  if (days <= 30) return { text: `Due in ${days}d`, hue: 'amber' };
  return { text: `Revisit ${d.toLocaleDateString(undefined, { month: 'short', year: 'numeric' })}`, hue: 'slate' };
};

// A timing signal's title reads "<what they said> · <why>"; every other kind is one phrase.
const signalHead = (s) => {
  const t = s.title || '';
  const i = s.kind === 'timing_note' ? t.indexOf(' · ') : -1;
  return i < 0 ? [t, null] : [t.slice(0, i), t.slice(i + 3)];
};

// [[kind, signals]…] in order of first appearance — the server sends strongest first.
const groupByKind = (signals) => {
  const by = new Map();
  signals.forEach((s) => { if (!by.has(s.kind)) by.set(s.kind, []); by.get(s.kind).push(s); });
  return [...by.entries()];
};

// Take signals out of the listed rows; a row left with none goes. → [rows, rows removed]
const dropSignals = (items, ids) => {
  let gone = 0;
  const out = items.flatMap((r) => {
    if (!(r.signals || []).some((s) => ids.has(s.id))) return [r];
    const left = r.signals.filter((s) => !ids.has(s.id));
    if (!left.length) { gone += 1; return []; }
    return [{ ...r, signals: left, n_signals: left.length }];
  });
  return [out, gone];
};

// Put them back (Undo, or a write that failed): into their row if it is still
// listed, otherwise the row returns where it was. `row` is the row as it was
// before they left. → [rows, rows added]
const restoreSignals = (items, row, index, ids) => {
  const at = items.findIndex((r) => rowKey(r) === rowKey(row));
  if (at >= 0) {
    const have = new Set(items[at].signals.map((s) => s.id));
    const signals = row.signals.filter((s) => have.has(s.id) || ids.has(s.id));
    const next = items.slice();
    next[at] = { ...items[at], signals, n_signals: signals.length };
    return [next, 0];
  }
  const signals = row.signals.filter((s) => ids.has(s.id));
  const next = items.slice();
  next.splice(Math.max(0, Math.min(index, next.length)), 0, { ...row, signals, n_signals: signals.length });
  return [next, 1];
};

window.SignalQueueLogic = { rowKey, queueDue, signalHead, groupByKind, dropSignals, restoreSignals };
